import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import i18n from "@viz-ui/i18n/i18n";
import SortableSwitch from "@viz-ui/components/SortableSwitch";
import SearchBox from "@viz-ui/components/searchBox";
import Button from "@paprika/button";
import Switch from "@paprika/switch";
import { UsageTracker } from "@visualizer/common/services/usageTracker/usageTracker";
import "./DataTableConfig.scss";

const DataTableConfig = ({ columnConfigs = [], fields = [], onSubmitClick, onCancelClick, onCloseClick }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [columnConfigsFormatted, setColumnConfigsFormatted] = useState([]);
  const [columnConfigsFormattedOriginal, setColumnConfigsFormattedOriginal] = useState([]);
  const [columnConfigsChanged, setColumnConfigsChanged] = useState([]);
  const [highlightLabel, setHighlightLabel] = useState("");
  const [showTheList, setShowTheList] = useState(true);
  const [showSearchBox, setShowSearchBox] = useState(true);

  const fieldsByName = useRef(new Map());
  const searchTimeoutRef = useRef(null);
  const fieldOnClickTimeoutRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const prevColumnConfigsPropRef = useRef(undefined);
  const columnConfigsChangedRef = useRef(columnConfigsChanged);
  columnConfigsChangedRef.current = columnConfigsChanged;

  const [configureTableViewRevamp, setConfigureTableViewRevamp] = useState(() => {
    if (typeof window !== "undefined" && window.AppConfig && window.AppConfig.features) {
      return window.AppConfig.features.configureTableViewRevamp === true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window.AppConfig && window.AppConfig.features) {
      setConfigureTableViewRevamp(window.AppConfig.features.configureTableViewRevamp === true);
    }
  }, []);

  const getDisplayNameByFieldName = useCallback(fieldName => {
    const fieldModel = fieldsByName.current.get(fieldName);
    if (!fieldModel) return;
    return fieldModel.displayName();
  }, []);

  const getDataTypeByFieldName = useCallback(fieldName => {
    const fieldModel = fieldsByName.current.get(fieldName);
    if (!fieldModel) return;
    return fieldModel.type();
  }, []);

  const filterFields = useCallback(
    (inputList, filterInput) => {
      let filteredList = inputList.filter(item => item.label.match(new RegExp(filterInput, "i")));
      const dragDisabled = filterInput.length > 0;
      filteredList = filteredList.map(item => {
        item.isDragDisabled = dragDisabled;
        item.highLight = item.label === highlightLabel;
        return item;
      });
      return filteredList;
    },
    [highlightLabel]
  );

  useLayoutEffect(() => {
    const newMap = new Map();
    fields.forEach(field => {
      newMap.set(field.name(), field);
    });
    fieldsByName.current = newMap;

    const columnConfigsPropChanged = prevColumnConfigsPropRef.current !== columnConfigs;
    if (columnConfigsPropChanged) {
      prevColumnConfigsPropRef.current = columnConfigs;
      setColumnConfigsChanged([...columnConfigs]);
    }

    if (configureTableViewRevamp && fields.length > 0) {
      const visibilitySource = columnConfigsPropChanged ? columnConfigs : columnConfigsChangedRef.current;
      const formatted = columnConfigs.map((element, index) => {
        const model = visibilitySource[index];
        const nameMatches = model && typeof model.name === "function" && model.name() === element.name();
        const visible = nameMatches ? model.visible() : element.visible();
        const wordWrap = nameMatches && typeof model.wordWrap === "function" ? model.wordWrap() : element.wordWrap();

        return {
          label: getDisplayNameByFieldName(element.name()),
          type: getDataTypeByFieldName(element.name()),
          checked: visible,
          wordWrap,
          isDragDisabled: false,
          highLight: false,
          originalIndex: index,
        };
      });

      setColumnConfigsFormattedOriginal(formatted);
      setColumnConfigsFormatted(formatted);
    } else if (!configureTableViewRevamp) {
      setColumnConfigsChanged([...columnConfigs]);
    }
  }, [columnConfigs, fields, configureTableViewRevamp, getDisplayNameByFieldName, getDataTypeByFieldName]);

  const areAllFieldsVisible = useMemo(() => {
    return columnConfigsChanged.filter(d => !d.visible()).length === 0;
  }, [columnConfigsChanged]);

  const handleAllFieldsVisibleClick = useCallback(
    event => {
      const newValue = !areAllFieldsVisible;

      const updatedConfigs = [...columnConfigsChanged];
      const updatedFormatted = [...columnConfigsFormatted];

      updatedConfigs.forEach((d, itemIndex) => {
        d.visible(newValue);
        // Find the corresponding item in columnConfigsFormatted by originalIndex
        const formattedItem = updatedFormatted.find(item => item.originalIndex === itemIndex);
        if (formattedItem) {
          formattedItem.checked = newValue;
        }
      });

      setColumnConfigsChanged(updatedConfigs);
      setColumnConfigsFormatted(updatedFormatted);

      if (event && event.keyCode === 32) {
        event.preventDefault();
      }
    },
    [areAllFieldsVisible, columnConfigsChanged, columnConfigsFormatted]
  );

  const handleFieldVisibleClick = useCallback(
    (field, event) => {
      if (configureTableViewRevamp) {
        const updatedConfigs = [...columnConfigsChanged];
        updatedConfigs.forEach((d, index) => {
          if (getDisplayNameByFieldName(d.name()) === field.label && index === field.originalIndex) {
            d.visible(!d.visible());
          }
        });
        setColumnConfigsChanged(updatedConfigs);
      } else {
        field.visible(!field.visible());
        setColumnConfigsChanged([...columnConfigsChanged]);
      }

      if (event && event.keyCode === 32) {
        event.preventDefault();
      }
    },
    [configureTableViewRevamp, columnConfigsChanged, getDisplayNameByFieldName]
  );

  const handleFieldPositionChange = useCallback(
    newSortedList => {
      let newColumnConfigs = [];
      newSortedList.forEach(field => {
        columnConfigsChanged.forEach((d, index) => {
          if (getDisplayNameByFieldName(d.name()) === field.label && index === field.originalIndex) {
            newColumnConfigs.push(d);
          }
        });
      });

      setColumnConfigsChanged(newColumnConfigs);
    },
    [columnConfigsChanged, getDisplayNameByFieldName]
  );

  const handleSearchTextChange = useCallback(
    e => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      const value = e ? e.target.value : "";
      setSearchTerm(value);

      const filtered = filterFields(columnConfigsFormattedOriginal, value);
      setColumnConfigsFormatted(filtered);
      setShowTheList(false);

      searchTimeoutRef.current = setTimeout(() => {
        setShowTheList(true);
      }, 500);
    },
    [columnConfigsFormattedOriginal, filterFields]
  );

  const handleFieldOnClick = useCallback(
    selectedItem => {
      const { label } = selectedItem;
      setHighlightLabel(label);
      setSearchTerm("");

      const updatedFormatted = columnConfigsFormattedOriginal.map(item => {
        item.isDragDisabled = false;
        item.highLight = item.label === label && item.originalIndex === selectedItem.originalIndex;
        return item;
      });

      setColumnConfigsFormatted(updatedFormatted);
      setHighlightLabel("");

      setShowTheList(false);
      setShowSearchBox(false);

      // Clear any existing timeout to prevent memory leaks
      if (fieldOnClickTimeoutRef.current) {
        clearTimeout(fieldOnClickTimeoutRef.current);
      }

      fieldOnClickTimeoutRef.current = setTimeout(() => {
        setShowTheList(true);
        setShowSearchBox(true);
      }, 500);
    },
    [columnConfigsFormattedOriginal]
  );

  const handleFieldsOnRender = useCallback(() => {
    const highlightElement = document.querySelector(".table-config__body .sortable-highlight");
    const container = document.querySelector(".table-config__body");

    if (highlightElement && container) {
      const position = highlightElement.offsetTop - container.offsetTop + container.scrollTop;
      container.scrollTo({ top: position, behavior: "smooth" });
      highlightElement.focus();

      // Clear any existing timeout to prevent memory leaks
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = setTimeout(() => {
        highlightElement.classList.remove("sortable-highlight");
      }, 1500);
    }
  }, []);

  const handleSubmitClick = useCallback(() => {
    UsageTracker.mark("dataTableConfig.submit");
    onSubmitClick({ columnConfigs: [...columnConfigsChanged] });
  }, [columnConfigsChanged, onSubmitClick]);

  const handleCloseClick = useCallback(() => {
    setSearchTerm("");
    const configureButton = document.querySelector(".action-buttons__configure-button");
    if (configureButton) {
      configureButton.focus();
    }
    onCloseClick();
  }, [onCloseClick]);

  const handleCancelClick = useCallback(() => {
    setSearchTerm("");
    const configureButton = document.querySelector(".action-buttons__configure-button");
    if (configureButton) {
      configureButton.focus();
    }
    onCancelClick();
  }, [onCancelClick]);

  // Cleanup all timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (fieldOnClickTimeoutRef.current) {
        clearTimeout(fieldOnClickTimeoutRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="table-config__main-container">
      <div className="viz-panel__header">
        <h2 tabIndex="-1" id="tableConfigHeadline">
          {i18n.t("_Table.DisplayConfig.Headline.Label_")}
        </h2>
        <button id="vizPanelHeader-closeButton" className="viz-panel__header-button" onClick={handleCloseClick}>
          <i className="acl-i-times"></i> {i18n.t("_General.Close.Label_")}
        </button>
      </div>

      {configureTableViewRevamp && (
        <div className="config-header">
          <div>
            {showSearchBox && (
              <SearchBox
                value={searchTerm}
                onChange={handleSearchTextChange}
                placeholder={i18n.t("_Table.DisplayConfig.SearchPlaceHolder.Label_")}
              />
            )}
          </div>
          {searchTerm.length === 0 && (
            <div className="toggle-all-fields">
              <span
                className={`config-header__all-fields ${!areAllFieldsVisible ? "config-header__all-fields--off" : ""}`}
              >
                {i18n.t("_Table.DisplayConfig.AllFields.Label_")}
              </span>
              <Switch
                size="small"
                isChecked={columnConfigsFormatted.length > 0 && areAllFieldsVisible}
                onClick={handleAllFieldsVisibleClick}
                className="table-enable-all-fields-toggle"
                a11yText={i18n.t("_Table.DisplayConfig.AllFields.Label_")}
              />
            </div>
          )}
        </div>
      )}

      <div className="table-config__body">
        {configureTableViewRevamp && showTheList && (
          <div className="chart-panels-divider fields-to-display">
            <div className="table-field-selector-container">
              <SortableSwitch
                items={columnConfigsFormatted}
                fieldTypeIcon={true}
                switchToggleCallback={handleFieldVisibleClick}
                fieldPositionChangeCallback={handleFieldPositionChange}
                fieldOnClickCallback={handleFieldOnClick}
                fieldsRenderCallback={handleFieldsOnRender}
              />
            </div>
          </div>
        )}

        {!configureTableViewRevamp && (
          <>
            <div className="chart-panels-divider">
              <div id="tableConfigFieldsLabel" className="title content-title-chart">
                {i18n.t("_Table.DisplayConfig.Columns.Label_")}
                <div
                  id="fieldVisibilityToggle-filterAll"
                  className={`filter-toggle pull-right ${!areAllFieldsVisible ? "toggle-off" : ""}`}
                  onClick={handleAllFieldsVisibleClick}
                  tabIndex="0"
                  onKeyDown={e => (e.keyCode === 13 || e.keyCode === 32) && handleAllFieldsVisibleClick(e)}
                >
                  <div className="filter-button"></div>
                </div>
                <span className="pull-right toggle-all-label">{i18n.t("_Table.DisplayConfig.AllColumns.Label_")}</span>
              </div>
            </div>
            <div className="chart-panels-divider">
              <div className="tableconfig-heading-labels">
                {i18n.t("_Table.DisplayConfig.Header.Columns.Label_")}
                <span className="pull-right">{i18n.t("_Table.DisplayConfig.Header.Move.Label_")}</span>
              </div>
            </div>
            <div className="chart-panels-divider fields-to-display">
              <div className="table-field-selector-container">
                {columnConfigsChanged.map((columnConfig, index) => (
                  <div key={index} id={`tableFieldItem-${index}`}>
                    <div className={`table-field-selector-item ${!columnConfig.visible() ? "disabled" : ""}`}>
                      <div
                        id={`fieldVisibilityToggle-${index}`}
                        className={`filter-toggle ${!columnConfig.visible() ? "toggle-off" : ""}`}
                        onClick={() => handleFieldVisibleClick(columnConfig)}
                        onKeyDown={e =>
                          (e.keyCode === 13 || e.keyCode === 32) && handleFieldVisibleClick(columnConfig, e)
                        }
                        tabIndex="0"
                      >
                        <div className="filter-button"></div>
                      </div>
                      <i
                        className={`panel-column-type__icon ${getFieldTypeIconClassName(
                          getDataTypeByFieldName(columnConfig.name())
                        )}`}
                      ></i>
                      <span className="table-field-selector-label">
                        {getDisplayNameByFieldName(columnConfig.name())}
                      </span>
                      <span className="icon-resize-vertical pull-right"></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="table-config__actions">
        {/* Wrapper span needed for Cypress selector compatibility: #applyTableConfigBtn > [role='button'] */}
        <span id="applyTableConfigBtn" className="table-config__action-btn">
          <Button onClick={handleSubmitClick} isSemantic={false}>
            {i18n.t("_ChartConfig.Apply.Label_")}
          </Button>
        </span>
        <span id="closeTableConfigPanelBtn" className="table-config__action-btn">
          <Button onClick={handleCancelClick} kind="minor" isSemantic={false}>
            {i18n.t("_General.Cancel.Label_")}
          </Button>
        </span>
      </div>
    </div>
  );
};

function getFieldTypeIconClassName(fieldType) {
  const typeIconMap = {
    character: "acl-i-font",
    numeric: "acl-i-hashtag",
    datetime: "acl-i-calendar",
    logical: "acl-i-check-square-o",
  };
  return typeIconMap[fieldType] || "acl-i-font";
}

DataTableConfig.propTypes = {
  columnConfigs: PropTypes.array.isRequired,
  fields: PropTypes.array.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
};

export default DataTableConfig;
